import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchKeywordSets, fetchPsap } from '@rsos/reduxDataStore';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { fetchUser } from '@rsos/sinatra';
import { loggedInSelector } from '../state/loggedInSelector';
import { type KeywordManagementState } from './rootReducer';

export const initialize = createAsyncThunk<
  void,
  void,
  { state: KeywordManagementState }
>('keywordManagement/initialize', async (_, { dispatch, getState }) => {
  try {
    await dispatch(fetchUser());
    dispatch(fetchKeywordSets());

    // KeywordManagement doesn't use the PSAP directly, but we have to fetch the
    // PSAP for the admin sidebar menu to work
    const orgName = organizationSelector(getState())?.name;
    dispatch(fetchPsap(`/psaps/${orgName}`));
  } catch (err) {
    if (loggedInSelector(getState())) {
      throw err;
    } else {
      window.location.href = '/';
    }
  }
});

export type InitializationState = {
  initializing: boolean;
  initialized: boolean;
  error: null | string;
};

const initialState: InitializationState = {
  initializing: true,
  initialized: false,
  error: null,
};

const initializationSlice = createSlice({
  name: 'initialization',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.error = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.error = null;
      })
      .addCase(initialize.rejected, (state, { error = {} }) => {
        state.initializing = false;
        state.initialized = false;
        state.error = error.message || 'Unknown error';
      });
  },
});

export const initializationReducer = initializationSlice.reducer;
export const initializationSelector = (state: KeywordManagementState) =>
  state.initialization;
